import {MenuItem} from './menu-model';

/**
 * The `MENU` variable represents a hierarchical menu structure used for navigation within an application.
 * It defines a series of menu items and their properties, such as labels, icons, links, submenus, and additional metadata.
 *
 * Each menu item can include:
 * - A unique `key` for identification purposes.
 * - A `label` to display the menu item name.
 * - An `icon` representing the menu item visually (optional).
 * - A `link` pointing to the navigation URL (optional).
 * - `collapsed` state indicating whether the menu or submenu should expand/collapse.
 * - A `badge` for showing additional information like counts or status (optional).
 * - A `subMenu` defining nested child menu items.
 * - `queryParams` for containing additional query parameters for links (optional).
 * - A `parentKey` referencing the parent menu key (for submenus).
 * - A flag `isTitle` to indicate if the item is a title and not clickable.
 *
 * This variable is used to render menu navigation dynamically based on its structure and hierarchy.
 */
export const MENU: MenuItem[] = [
  {
    key: 'navigation',
    label: 'navigation',
    isTitle: true,
  },
  {
    key: 'dashboards',
    icon: 'uil-home-alt',
    label: 'Dashboards',
    collapsed: true,
    subMenu: [
      {
        key: 'dashboards-onboard',
        label: 'Onboard',
        link: '/dashboards/onboard',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-home',
        label: 'Financial Dashboard',
        link: '/home',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-inventory',
        label: 'Inventory Dashboard',
        link: '/inventory/dashboard',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-ar',
        label: 'AR Dashboard',
        link: '/dashboards/ar',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-ap',
        label: 'AP Dashboard',
        link: '/dashboards/ap',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-gusto',
        label: 'Gusto Dashboard',
        link: '/payroll/gusto-dashboard',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-stripe',
        label: 'Stripe Dashboard',
        link: '/stripe/dashboard',
        collapsed: true,
        parentKey: 'dashboards',
      },
    ],
  },
  {
    key: 'schedule',
    icon: 'uil-calendar-alt',
    label: 'Schedule',
    link: '/schedule',
    collapsed: true,
  },
  {
    key: 'entity',
    icon: 'uil-building',
    label: 'Entity',
    collapsed: true,
    subMenu: [
      {
        key: 'entity-list',
        label: 'Entity List',
        link: '/entity-list',
        parentKey: 'entity',
      },
      {
        key: 'entity-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'entity',
        subMenu: [
          {
            key: 'entity-history-report',
            label: 'Entity History',
            link: '/transaction-reports/entity-history-report',
            parentKey: 'entity-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'crm',
    icon: 'uil-star',
    label: 'CRM',
    collapsed: true,
    subMenu: [
      {
        key: 'crm-dashboard',
        label: 'Dashboard',
        link: '/crm',
        parentKey: 'crm',
      },
      {
        key: 'crm-entities',
        label: 'Customers & Vendors',
        link: '/crm/entities',
        parentKey: 'crm',
      },
      {
        key: 'crm-pipeline',
        label: 'Pipeline',
        link: '/crm/pipeline',
        parentKey: 'crm',
      },
      {
        key: 'crm-opportunities',
        label: 'Opportunities',
        link: '/crm/opportunities',
        parentKey: 'crm',
      },
      {
        key: 'crm-interactions',
        label: 'Interactions',
        link: '/crm/interactions',
        parentKey: 'crm',
      },
      {
        key: 'crm-reports',
        label: 'Reports',
        icon: 'uil-file-alt',
        collapsed: true,
        parentKey: 'crm',
        subMenu: [
          {
            key: 'crm-report-pipeline',
            label: 'Pipeline Summary',
            link: '/crm/reports/pipeline',
            parentKey: 'crm-reports',
          },
          {
            key: 'crm-report-won-lost',
            label: 'Won / Lost Analysis',
            link: '/crm/reports/won-lost',
            parentKey: 'crm-reports',
          },
          {
            key: 'crm-report-activity',
            label: 'Salesperson Activity',
            link: '/crm/reports/activity',
            parentKey: 'crm-reports',
          },
          {
            key: 'crm-report-opportunity-detail',
            label: 'Opportunity Detail',
            link: '/crm/reports/opportunity-detail',
            parentKey: 'crm-reports',
          },
          {
            key: 'crm-report-followup',
            label: 'Follow-Up Required',
            link: '/crm/reports/followup',
            parentKey: 'crm-reports',
          },
          {
            key: 'crm-report-pipeline-aging',
            label: 'Pipeline Aging',
            link: '/crm/reports/pipeline-aging',
            parentKey: 'crm-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'receivables',
    icon: 'uil-money-withdraw',
    label: 'Receivables',
    collapsed: true,
    subMenu: [
      {
        key: 'receivables-transactions',
        label: 'Transactions',
        collapsed: true,
        parentKey: 'receivables',
        subMenu: [
          {
            key: 'receivables-invoices',
            label: 'Invoices',
            link: '/transactions/invoice/list/ar',
            parentKey: 'receivables',
          },
          {
            key: 'receivables-payments',
            label: 'Payments',
            link: '/transactions/payment/list/ar',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'receivables',
          },
          {
            key: 'receivables-recurring',
            label: 'Recurring',
            link: '/transactions/invoice/recurring/ar',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'receivables',
          },
        ],
      },
      {
        key: 'receivables-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'receivables',
        subMenu: [
          {
            key: 'receivables-aging-report',
            label: 'Aging',
            link: '/transaction-reports/aging/ar',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'receivables-reports',
          },
          {
            key: 'receivables-reconciliation-report',
            label: 'Reconciliation',
            link: '/transaction-reports/entity-reconciliation/ar',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'receivables-reports',
          },
          {
            key: 'receivables-invoices-report',
            label: 'Invoices',
            link: '/transaction-reports/invoices/ar',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'receivables-reports',
          },
          {
            key: 'receivables-payment-report',
            label: 'Payment',
            link: '/transaction-reports/payments/ar',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'receivables-reports',
          },
          {
            key: 'receivables-stripe-transaction-detail',
            label: 'Stripe Transaction Detail',
            link: '/transaction-reports/stripe-transaction-detail',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'receivables-reports',
          },
          {
            key: 'receivables-statement-report',
            label: 'Statement',
            link: '/transaction-reports/statements',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'receivables-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'payables',
    icon: 'uil-money-insert',
    label: 'Payables',
    collapsed: true,
    subMenu: [
      {
        key: 'payables-transactions',
        label: 'Transactions',
        collapsed: true,
        parentKey: 'payables',
        subMenu: [
          {
            key: 'payables-invoices',
            label: 'Invoices',
            link: '/transactions/invoice/list/ap',
            parentKey: 'payables-transactions',
          },
          {
            key: 'payables-payments',
            label: 'Payments',
            link: '/transactions/payment/list/ap',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'payables-transactions',
          },
          {
            key: 'payables-issue-payments',
            label: 'Issue Payments',
            link: '/transactions/issue-payments',
            parentKey: 'payables-transactions',
          },
          {
            key: 'payables-recurring',
            label: 'Recurring',
            link: '/transactions/invoice/recurring/ap',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'payables-transactions',
          },
        ],
      },
      {
        key: 'payables-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'payables',
        subMenu: [
          {
            key: 'payables-aging-report',
            label: 'Aging',
            link: '/transaction-reports/aging/ap',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'payables-reports',
          },
          {
            key: 'payables-reconciliation-report',
            label: 'Reconciliation',
            link: '/transaction-reports/entity-reconciliation/ap',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'payables-reports',
          },
          {
            key: 'payables-invoices-report',
            label: 'Invoices',
            link: '/transaction-reports/invoices/ap',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'payables-reports',
          },
          {
            key: 'payables-payment-report',
            label: 'Payment',
            link: '/transaction-reports/payments/ap',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'payables-reports',
          },
          {
            key: 'payables-stripe-transaction-detail',
            label: 'Stripe Transaction Detail',
            link: '/transaction-reports/stripe-transaction-detail',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'payables-reports',
          },
          {
            key: 'payables-check-register',
            label: 'Check Register',
            link: '/transaction-reports/check-register',
            parentKey: 'payables-reports',
          },
          {
            key: 'payables-tax-report',
            label: 'Tax',
            link: '/transaction-reports/tax',
            parentKey: 'payables-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'bank',
    icon: 'bi bi-bank2',
    label: 'Bank',
    collapsed: true,
    subMenu: [
      {
        key: 'bank-transactions',
        label: 'Transactions',
        collapsed: true,
        parentKey: 'bank',
        subMenu: [
          {
            key: 'bank-reconciliation',
            label: 'Reconciliation',
            link: '/transactions/bank-reconciliation/bank-reconciliation-list',
            parentKey: 'bank-transactions',
          },
          {
            key: 'bank-import',
            label: 'Import Transactions',
            link: '/transactions/bank-import',
            parentKey: 'bank-transactions',
          },
        ],
      },
    ],
  },
  {
    key: 'ledger',
    icon: 'uil-columns',
    label: 'General Ledger',
    collapsed: true,
    subMenu: [
      {
        key: 'ledger-transactions',
        label: 'Transactions',
        collapsed: true,
        parentKey: 'ledger',
        subMenu: [
          {
            key: 'ledger-journal-entries',
            label: 'Journal Entries',
            link: '/transactions/entry/list',
            parentKey: 'ledger-transactions',
          },
          {
            key: 'ledger-recurring-journal-entries',
            label: 'Recurring Entries',
            link: '/transactions/entry/recurring',
            parentKey: 'ledger-transactions',
          },
          {
            key: 'ledger-statistical-journal-entries',
            label: 'Statistical Entries',
            link: '/transactions/statistical/list',
            parentKey: 'ledger-transactions',
          },
          {
            key: 'ledger-statistical-recurring-journal-entries',
            label: 'Statistical Recurring Entries',
            link: '/transactions/statistical/recurring-list',
            parentKey: 'ledger-transactions',
          },
        ],
      },
      {
        key: 'ledger-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'ledger',
        subMenu: [
          {
            key: 'ledger-journal-entry-report',
            label: 'Journal Entries',
            link: '/ledger/reports/journal-entry',
            parentKey: 'ledger-reports',
          },
          {
            key: 'trial-balance-report',
            label: 'Trial Balance',
            link: '/transaction-reports/trial-balance',
            parentKey: 'ledger-reports',
          },
          {
            key: 'taxes-collected-report',
            label: 'Taxes Collected',
            link: '/transaction-reports/tax',
            parentKey: 'ledger-reports',
          },
          {
            key: 'margin-report',
            label: 'Margin',
            link: '/transaction-reports/margin-report',
            parentKey: 'ledger-reports',
          },
          {
            key: 'transaction-detail-report',
            label: 'Transaction Detail',
            link: '/transaction-reports/transaction-detail',
            parentKey: 'ledger-reports',
          },
        ],
      },
      {
        key: 'ledger-financials',
        label: 'Financials',
        collapsed: true,
        parentKey: 'ledger',
        subMenu: [
          {
            key: 'cash-flow-report',
            label: 'Cash Flow',
            link: '/transaction-reports/cash-flow',
            parentKey: 'ledger-financials',
          },
          {
            key: 'income-statement-report',
            label: 'Income Statement',
            link: '/transaction-reports/income-statement',
            parentKey: 'ledger-financials',
          },
          {
            key: 'consolidated-income-statement-report',
            label: 'Consolidated Income Statement',
            link: '/transaction-reports/consolidated-income-statement',
            parentKey: 'ledger-financials',
          },
          {
            key: 'balance-sheet-report',
            label: 'Balance Sheet',
            link: '/transaction-reports/balance-sheet',
            parentKey: 'ledger-financials',
          },
        ],
      },
    ],
  },
  {
    key: 'payroll',
    icon: 'uil-user',
    label: 'Payroll',
    collapsed: true,
    subMenu: [
      {
        key: 'payroll-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'payroll',
        subMenu: [
          {
            key: 'employee-detail-report',
            label: 'Employee Pay Detail',
            link: '/payroll/transaction-reports/employee-detail',
            parentKey: 'payroll-reports',
          },
        ],
      },
      {
        key: 'payroll-setup',
        label: 'Setup',
        collapsed: true,
        parentKey: 'payroll',
        subMenu: [
          {
            key: 'employee-setup',
            label: 'Employee',
            link: '/payroll/employee-list',
            parentKey: 'payroll-setup',
          },
          {
            key: 'department-setup',
            label: 'Departments',
            link: '/payroll/department-list',
            parentKey: 'payroll-setup',
          },
          {
            key: 'employee-types-setup',
            label: 'Employee Types',
            link: '/payroll/employee-type-list',
            parentKey: 'payroll-setup',
          },
        ],
      },
      {
        key: 'payroll-settings',
        label: 'Settings',
        collapsed: true,
        parentKey: 'payroll',
        subMenu: [
          {
            key: 'payroll-provider-config',
            label: 'Provider / Gusto',
            link: '/payroll/provider-config',
            parentKey: 'payroll-settings',
          },
        ],
      },
    ],
  },
  {
    key: 'fixed-assets',
    icon: 'uil-briefcase',
    label: 'Fixed Assets',
    collapsed: true,
    subMenu: [
      {
            key: 'fixed-asset-items',
            label: 'Asset List',
            link: '/fixed-assets/fixed-asset-list',
        parentKey: 'fixed-assets'
      },
      {
        key: 'fixed-assets-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'fixed-assets',
        subMenu: [
          {
            key: 'fixed-asset-disposal-report',
            label: 'Disposal Report',
            link: 'Reports/fixed-assets/disposals-report',
            parentKey: 'fixed-asset-reports',
          },
          {
            key: 'fixed-asset-depreciation-report',
            label: 'Depreciation Schedule Report',
            link: 'Reports/fixed-assets/depreciation-schedule-report',
            parentKey: 'fixed-asset-reports',
          },
          {
            key: 'fixed-asset-register-report',
            label: 'Fixed Asset Register',
            link: 'Reports/fixed-assets/asset-register',
            parentKey: 'fixed-asset-reports',
          },
          {
            key: 'fixed-asset-entries-report',
            label: 'Fixed Asset GL Entries',
            link: 'Reports/fixed-assets/gl-entries',
            parentKey: 'fixed-asset-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'setup',
    icon: 'uil-cog',
    label: 'System Setup',
    collapsed: true,
    subMenu: [
      {
        key: 'financial-setup',
        label: 'Financial',
        collapsed: true,
        parentKey: 'setup',
        subMenu: [
          {
            key: 'account-setup',
            label: 'Accounts',
            link: '/admin/account-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'account-margin-setup',
            label: 'Account Margins',
            link: '/admin/account-margin-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'account-subtotal-setup',
            label: 'Account Subtotals',
            link: '/admin/account-subtotal-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'bank-setup',
            label: 'Bank Accounts',
            link: '/admin/bank-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'budget-setup',
            label: 'Budget',
            link: '/admin/budget-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'check-stock-setup',
            label: 'Check Stock',
            link: '/admin/check-stock-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'currency-setup',
            label: 'Currency',
            link: '/admin/currency-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'currency-rate-setup',
            label: 'Currency Rate',
            link: '/admin/currency-rate-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'payment-type-setup',
            label: 'Payment Type',
            link: '/admin/payment-type-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'stripe-admin-setup',
            label: 'Stripe Setup',
            link: '/admin/stripe-admin-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'tax-setup',
            label: 'Tax Codes',
            link: '/admin/tax-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'tax-rate-setup',
            label: 'Tax Rates',
            link: '/admin/tax-rate-list',
            parentKey: 'financial-setup',
          },
          {
            key: 'terms-setup',
            label: 'Terms',
            link: '/admin/term-list',
            parentKey: 'financial-setup',
          },
        ],
      },
      {
        key: 'fixed-assets-setup',
        label: 'Fixed Assets',
        collapsed: true,
        parentKey: 'setup',
        subMenu: [
          {
            key: 'fixed-assets-config',
            label: 'Fixed Assets Configuration',
            link: '/admin/fixed-assets/fixed-assets-config',
            parentKey: 'fixed-assets-setup',
          }
        ],
      },
      {
        key: 'general-setup',
        label: 'General',
        collapsed: true,
        parentKey: 'setup',
        subMenu: [
          {
            key: 'companies-setup',
            label: 'Company',
            link: '/admin/company-list',
            parentKey: 'general-setup',
          },
          {
            key: 'company-access-setup',
            label: 'Company Access',
            link: '/admin/company-access-list',
            parentKey: 'general-setup',
          },
          {
            key: 'entity-type-setup',
            label: 'Entity Type',
            link: '/admin/entity-type-list',
            parentKey: 'general-setup',
          },
          {
            key: 'entity-setup',
            label: 'Entity',
            link: '/entity-list',
            parentKey: 'general-setup',
          },
          {
            key: 'entity-access-setup',
            label: 'Entity Access',
            link: '/admin/entity-access-list',
            parentKey: 'general-setup',
          },
          {
            key: 'reporting-field-setup',
            label: 'Reporting Field',
            link: '/admin/reporting-field-list',
            parentKey: 'general-setup',
          },
          {
            key: 'reporting-field-type-setup',
            label: 'Reporting Field Type',
            link: '/admin/reporting-field-type-list',
            parentKey: 'general-setup',
          },
          {
            key: 'salesperson-setup',
            label: 'Salesperson',
            link: '/admin/salesperson-list',
            parentKey: 'general-setup',
          },
        ],
      },
      {
        key: 'inventory-setup',
        label: 'Inventory',
        collapsed: true,
        parentKey: 'setup',
        subMenu: [
          {
            key: 'inventory-item-setup',
            label: 'Inventory Item',
            link: '/admin/item-list',
            parentKey: 'inventory-setup',
          },
          {
            key: 'create-bom-setup',
            label: 'Create BOM',
            link: '/inventory/bill-of-materials/bom-list',
            parentKey: 'inventory-setup',
          },
          {
            key: 'assemble-bom-setup',
            label: 'Assemble BOM',
            link: '/inventory/assemble/list',
            parentKey: 'inventory-setup',
          },
          {
            key: 'item-pricing-setup',
            label: 'Item Pricing',
            link: '/admin/item-pricing-list',
            parentKey: 'inventory-setup',
          },
          {
            key: 'item-category-setup',
            label: 'Item Category',
            link: '/admin/item-category-list',
            parentKey: 'inventory-setup',
          },
          {
            key: 'item-group-setup',
            label: 'Item Group',
            link: '/admin/item-group-list',
            parentKey: 'inventory-setup',
          },
          {
            key: 'item-type-setup',
            label: 'Item Type',
            link: '/admin/item-type-list',
            parentKey: 'inventory-setup',
          },
          {
            key: 'uom-setup',
            label: 'Unit of Measure',
            link: '/admin/uom-list',
            parentKey: 'inventory-setup',
          },
        ],
      },
      {
        key: 'location-setup',
        label: 'Location',
        collapsed: true,
        parentKey: 'general-setup',
        subMenu: [
          {
            key: 'location-setup',
            label: 'Location',
            link: '/admin/location-list',
            parentKey: 'location-setup',
          },
          {
            key: 'location-access-setup',
            label: 'Location Access',
            link: '/admin/location-access-list',
            parentKey: 'location-setup',
          },
          {
            key: 'location-group-setup',
            label: 'Location Group',
            link: '/admin/location-group-list',
            parentKey: 'location-setup',
          },
        ],
      },
      {
        key: 'user-setup',
        label: 'Users',
        collapsed: true,
        parentKey: 'setup',
        subMenu: [
          {
            key: 'user-setup',
            label: 'User',
            link: '/admin/sys-user/list',
            parentKey: 'user-setup',
          },
          {
            key: 'user-preferences-setup',
            label: 'Preferences',
            link: '/admin/user-profile',
            parentKey: 'user-setup',
          },
        ],
      },
      {
        key: 'other-setup',
        label: 'Other',
        collapsed: true,
        parentKey: 'setup',
        subMenu: [
          {
            key: 'user-setup',
            label: 'Audit Trail Viewer',
            link: '/admin/audit-trail-viewer-form',
            parentKey: 'user-setup',
          },
          {
            key: 'admin-operations',
            label: 'Admin Operations',
            link: '/admin/operations',
            parentKey: 'other-setup',
          },
        ],
      },
    ],
  },

];


/**
 * Represents the operations menu configuration.
 * Each menu item is structured to define application navigation options, including top-level menu items,
 * submenu items, icons, labels, and links.
 *
 * Properties:
 * - `key`: A unique string identifier for the menu item.
 * - `label`: A string representing the display text for the menu item.
 * - `icon`: (Optional) A string representing the CSS class or icon name for the menu item.
 * - `isTitle`: (Optional) A boolean indicating if the menu item is a title without a link.
 * - `collapsed`: (Optional) A boolean specifying if the menu item should be initially collapsed.
 * - `badge`: (Optional) An object containing `variant` and `text` properties for displaying badges.
 * - `subMenu`: (Optional) An array of submenu items that further nest navigation options.
 * - `link`: (Optional) A string specifying the URL for navigation.
 * - `parentKey`: (Optional) A string indicating the key of the parent menu item.
 *
 * Usage:
 * This configuration can be used to dynamically construct a navigation menu for an operations dashboard
 * or related applications.
 *
 * Example Structure:
 * The menu can include hierarchical configurations where each item can include nested menus.
 * The structure takes into account user interface preferences, e.g., icons, collapsing state, and badges.
 */
export const OPSMENU: MenuItem[] = [
  {
    key: 'navigation',
    label: 'navigation',
    isTitle: true,
  },
  {
    key: 'dashboards',
    icon: 'uil-home-alt',
    label: 'Dashboards',
    collapsed: true,
    subMenu: [
      {
        key: 'dashboards-onboard',
        label: 'Onboard',
        link: '/dashboards/onboard',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-home',
        label: 'Financial Dashboard',
        link: '/home',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-inventory',
        label: 'Inventory Dashboard',
        link: '/inventory/dashboard',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-ar',
        label: 'AR Dashboard',
        link: '/dashboards/ar',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-ap',
        label: 'AP Dashboard',
        link: '/dashboards/ap',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-gusto',
        label: 'Gusto Dashboard',
        link: '/payroll/gusto-dashboard',
        collapsed: true,
        parentKey: 'dashboards',
      },
      {
        key: 'dashboards-stripe',
        label: 'Stripe Dashboard',
        link: '/stripe/dashboard',
        collapsed: true,
        parentKey: 'dashboards',
      },
    ],
  },
  {
    key: 'schedule',
    icon: 'uil-calendar-alt',
    label: 'Schedule',
    link: '/schedule',
    collapsed: true,
  },
  {
    key: 'entity',
    icon: 'uil-building',
    label: 'Entity',
    collapsed: true,
    subMenu: [
      {
        key: 'entity-list',
        label: 'Entity List',
        link: '/entity-list',
        parentKey: 'entity-setup',
      },
      {
        key: 'entity-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'entity',
        subMenu: [
          {
            key: 'entity-history-report',
            label: 'Entity History',
            link: '/transaction-reports/entity-history-report',
            parentKey: 'entity-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'crm',
    icon: 'uil-star',
    label: 'CRM',
    collapsed: true,
    subMenu: [
      {
        key: 'crm-dashboard-ops',
        label: 'Dashboard',
        link: '/crm',
        parentKey: 'crm',
      },
      {
        key: 'crm-entities-ops',
        label: 'Customers & Vendors',
        link: '/crm/entities',
        parentKey: 'crm',
      },
      {
        key: 'crm-pipeline-ops',
        label: 'Pipeline',
        link: '/crm/pipeline',
        parentKey: 'crm',
      },
      {
        key: 'crm-opportunities-ops',
        label: 'Opportunities',
        link: '/crm/opportunities',
        parentKey: 'crm',
      },
      {
        key: 'crm-interactions-ops',
        label: 'Interactions',
        link: '/crm/interactions',
        parentKey: 'crm',
      },
      {
        key: 'crm-reports-ops',
        label: 'Reports',
        icon: 'uil-file-alt',
        collapsed: true,
        parentKey: 'crm',
        subMenu: [
          {
            key: 'crm-report-pipeline-ops',
            label: 'Pipeline Summary',
            link: '/crm/reports/pipeline',
            parentKey: 'crm-reports-ops',
          },
          {
            key: 'crm-report-won-lost-ops',
            label: 'Won / Lost Analysis',
            link: '/crm/reports/won-lost',
            parentKey: 'crm-reports-ops',
          },
          {
            key: 'crm-report-activity-ops',
            label: 'Salesperson Activity',
            link: '/crm/reports/activity',
            parentKey: 'crm-reports-ops',
          },
          {
            key: 'crm-report-opportunity-detail-ops',
            label: 'Opportunity Detail',
            link: '/crm/reports/opportunity-detail',
            parentKey: 'crm-reports-ops',
          },
          {
            key: 'crm-report-followup-ops',
            label: 'Follow-Up Required',
            link: '/crm/reports/followup',
            parentKey: 'crm-reports-ops',
          },
          {
            key: 'crm-report-pipeline-aging-ops',
            label: 'Pipeline Aging',
            link: '/crm/reports/pipeline-aging',
            parentKey: 'crm-reports-ops',
          },
        ],
      },
    ],
  },
  {
    key: 'sales',
    icon: 'uil-money-withdraw',
    label: 'Sales',
    collapsed: true,
    subMenu: [
      {
        key: 'sales-transactions',
        label: 'Transactions',
        parentKey: 'sales',
        collapsed: true,
        subMenu: [
          {
            key: 'sales-orders',
            label: 'Orders',
            link: '/transactions/invoice/list/salesorders',
            parentKey: 'sales-transactions',
          },
          {
            key: 'sales-quotes',
            label: 'Quotes',
            link: '/transactions/invoice/list/quotes',
            parentKey: 'sales-transactions',
          },
          {
            key: 'shipments',
            label: 'Shipments',
            link: '/transactions/shipment/list/ar',
            queryParams: {type: 'shipments', subLedger: 'ar'},
            parentKey: 'sales-transactions',
          },
          {
            key: 'receivables-invoice',
            label: 'Invoice',
            link: '/transactions/invoice/list/ar',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'sales-transactions',
          },
        ],
      },
      {
        key: 'sales-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'sales',
        subMenu: [
          {
            key: 'sale-aging-report',
            label: 'Aging',
            link: '/transaction-reports/aging/ar',
            queryParams: {type: 'ar', subLedger: 'ar'},
            parentKey: 'sales-reports',
          },
          {
            key: 'sales-orders-report',
            label: 'Orders',
            link: '/transaction-reports/invoices/so',
            queryParams: {subLedger: 'so', toDate: ''},
            parentKey: 'sales-reports',
          },
          {
            key: 'sales-shipments-report',
            label: 'Shipments',
            link: '/transaction-reports/invoices/ss',
            queryParams: {subLedger: 'ss', toDate: ''},
            parentKey: 'sales-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'purchases',
    icon: 'uil-money-insert',
    label: 'Purchases',
    collapsed: true,
    subMenu: [
      {
        key: 'purchases-transactions',
        label: 'Transactions',
        collapsed: true,
        parentKey: 'purchases',
        subMenu: [
          {
            key: 'purchases-orders',
            label: 'Orders',
            link: '/transactions/invoice/list/purchaseorders',
            queryParams: {subLedger: 'purchaseorders', type: 'purchaseorders'},
            parentKey: 'purchases-transactions',
          },
          {
            key: 'purchases-receiving',
            label: 'Receiving',
            link: '/transactions/shipment/list/ap',
            queryParams: {type: 'shipments', subLedger: 'ap'},
            parentKey: 'purchases-transactions',
          },
          {
            key: 'payables-Invoices',
            label: 'Invoices',
            link: '/transactions/invoice/list/ap',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'purchases-transactions',
          },
        ],
      },
      {
        key: 'purchase-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'purchases',
        subMenu: [
          {
            key: 'purchases-aging-report',
            label: 'Aging',
            link: '/transaction-reports/aging/ap',
            queryParams: {type: 'ap', subLedger: 'ap'},
            parentKey: 'purchases-reports',
          },
          {
            key: 'payables-orders-report',
            label: 'Orders',
            link: '/transaction-reports/invoices/po',
            queryParams: {type: 'po', subLedger: 'po'},
            parentKey: 'purchases-reports',
          },
          {
            key: 'purchases-receiving-report',
            label: 'Receiving',
            link: '/transaction-reports/invoices/ps',
            queryParams: {type: 'ps', subLedger: 'ps'},
            parentKey: 'purchases-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'pos',
    icon: 'bi bi-cart-plus',
    label: 'POS',
    collapsed: true,
    subMenu: [
      {
        key: 'pos-transactions',
        label: 'Transactions',
        collapsed: true,
        parentKey: 'pos',
        subMenu: [
          {
            key: 'pos-data-browser',
            label: 'POS Data Browse',
            link: '/pos/pos-data-browser-list/pos-data-browser-list',
            parentKey: 'pos-transactions',
          },
        ],
      },
      {
        key: 'pos-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'pos',
        subMenu: [
          {
            key: 'pos-some-report',
            label: 'Some Cool POS Report',
            parentKey: 'pos-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'activity',
    icon: 'bi bi-clock-history',
    label: 'Timesheets',
    collapsed: true,
    subMenu: [
      {
        key: 'activity-transactions',
        label: 'Transactions',
        collapsed: true,
        parentKey: 'activity',
        subMenu: [
          {
            key: 'activity-timesheets',
            label: 'Time Sheets',
            link: '/timesheet/timesheet-list',
            parentKey: 'activity-transactions',
          },
          {
            key: 'activity-timesheet-approval',
            label: 'Timesheet Approval',
            link: '/timesheet/timesheet-approval-list',
            parentKey: 'activity-transactions',
          },
        ],
      },
    ],
  },
  {
    key: 'projects',
    icon: 'uil-chart-bar',
    label: 'Projects',
    collapsed: true,
    subMenu: [
      {
        key: 'projects-dashboard',
        label: 'Dashboard',
        icon: 'uil-tachometer-fast',
        link: '/projects/project-dashboard',
        collapsed: true,
        parentKey: 'projects',
      },
      {
        key: 'projects-list',
        label: 'Projects',
        icon: 'uil-list-ul',
        link: '/projects/project-list',
        collapsed: true,
        parentKey: 'projects',
      },
      {
        key: 'projects-new',
        label: 'New Project',
        icon: 'uil-plus-circle',
        link: '/projects/project-form',
        collapsed: true,
        parentKey: 'projects',
      },
      {
        key: 'projects-settings',
        label: 'Settings',
        icon: 'uil-setting',
        collapsed: true,
        parentKey: 'projects',
        subMenu: [
          {
            key: 'projects-personnel-rates',
            label: 'Project Personnel Rates',
            link: '/projects/settings/pm-user-billing-rate-list',
            parentKey: 'projects-settings',
          },
        ],
      },
      {
        key: 'projects-reports',
        label: 'Reports',
        icon: 'uil-file-alt',
        collapsed: true,
        parentKey: 'projects',
        subMenu: [
          {
            key: 'projects-report-status',
            label: 'Project Status Summary',
            link: '/projects/reports/project-status',
            parentKey: 'projects-reports',
          },
          {
            key: 'projects-report-overbudget',
            label: 'Over-Budget Tasks',
            link: '/projects/reports/over-budget-tasks',
            parentKey: 'projects-reports',
          },
          {
            key: 'projects-report-detail',
            label: 'Project Detail',
            link: '/projects/reports/project-detail',
            parentKey: 'projects-reports',
          },
          {
            key: 'projects-report-time',
            label: 'Time by Project',
            link: '/projects/reports/project-time',
            parentKey: 'projects-reports',
          },
          {
            key: 'projects-report-profitability',
            label: 'Project Profitability',
            link: '/projects/reports/project-profitability',
            parentKey: 'projects-reports',
          },
          {
            key: 'projects-report-milestone',
            label: 'Milestone / Billing Schedule',
            link: '/projects/reports/project-milestone',
            parentKey: 'projects-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'workorders',
    icon: 'uil-wrench',
    label: 'Work Orders',
    collapsed: true,
    subMenu: [
      {
        key: 'workorders-dashboard',
        label: 'Dashboard',
        link: '/work-orders/dashboard',
        parentKey: 'workorders',
      },
          {
            key: 'workorders-list',
            label: 'Work Orders List',
            link: '/work-orders/work-orders-list',
            parentKey: 'workorders',
          },
          {
            key: 'workorders-schedule',
            label: 'Dispatch / Schedule',
            link: '/work-orders/schedule',
            parentKey: 'workorders',
          },
          {
            key: 'workorders-assets',
            label: 'Assets',
            link: '/work-orders/assets/asset-list',
            parentKey: 'workorders',
          },
          {
            key: 'workorders-pm-schedules',
            label: 'PM Schedules',
            link: '/work-orders/pm-schedules/pm-schedule-list',
            parentKey: 'workorders',
      },
      {
        key: 'workorders-settings',
        label: 'Setup',
        collapsed: true,
        parentKey: 'workorders',
        subMenu: [
          {
            key: 'workorders-wo-types',
            label: 'WO Types',
            link: '/work-orders/settings/wo-type-list',
            parentKey: 'workorders-settings',
          },
          {
            key: 'workorders-wo-categories',
            label: 'WO Categories',
            link: '/work-orders/settings/wo-category-list',
            parentKey: 'workorders-settings',
          },
          {
            key: 'workorders-billing-rates',
            label: 'Technician Billing Rates',
            link: '/work-orders/settings/wo-user-billing-rate-list',
            parentKey: 'workorders-settings',
          },
          {
            key: 'workorders-checklists',
            label: 'Checklist Templates',
            link: '/work-orders/settings/checklist-template-list',
            parentKey: 'workorders-settings',
          },
        ],
      },
      {
        key: 'workorders-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'workorders',
        subMenu: [
          {
            key: 'workorders-report',
            label: 'Work Orders',
            link: '/activity/transaction-reports/workorder-report',
            parentKey: 'workorders-reports',
          },
        ],
      },
    ],
  },
  {
    key: 'inventory',
    icon: 'bi bi-boxes',
    label: 'inventory',
    collapsed: true,
    subMenu: [
      {
        key: 'inventory-transactions',
        label: 'Transactions',
        collapsed: true,
        parentKey: 'inventory',
        subMenu: [
          {
            key: 'inventory-count',
            label: 'Counts',
            link: '/inventory/count/list',
            parentKey: 'inventory-transactions',
          },
          {
            key: 'inventory-transfer',
            label: 'Transfers',
            link: '/inventory/transfer/list',
            parentKey: 'inventory-transactions',
          },
        ],
      },
      {
        key: 'inventory-settings',
        label: 'Setup',
        collapsed: true,
        parentKey: 'inventory',
        subMenu: [
          {
            key: 'inventory-assemble-bom',
            label: 'Assemble BOM',
            link: '/inventory/assemble/list',
            parentKey: 'inventory-settings',
          },
          {
            key: 'inventory-bom',
            label: 'Bill of Materials',
            link: '/inventory/bill-of-materials/bom-list',
            parentKey: 'inventory-settings',
          },
          {
            key: 'inventory-items',
            label: 'Items',
            link: '/admin/item-list',
            parentKey: 'inventory-settings',
          },
          {
            key: 'inventory-item-category',
            label: 'Item Categories',
            link: '/admin/item-category-list',
            parentKey: 'inventory-settings',
          },
          {
            key: 'inventory-item-pricing',
            label: 'Item Pricing',
            link: '/admin/item-pricing-list',
            parentKey: 'inventory-settings',
          },
          {
            key: 'inventory-locations',
            label: 'Locations',
            link: '/admin/location-list',
            parentKey: 'inventory-settings',
          },
        ],
      },
      {
        key: 'inventory-reports',
        label: 'Reports',
        collapsed: true,
        parentKey: 'inventory',
        subMenu: [
          {
            key: 'inventory-assembly-report',
            label: 'Assemblies',
            link: '/inventory/transaction-reports/assembly',
            parentKey: 'inventory-reports',
          },
          {
            key: 'inventory-balances-report',
            label: 'Balances',
            link: '/inventory/transaction-reports/balance',
            parentKey: 'inventory-reports',
          },
          {
            key: 'inventory-counts-report',
            label: 'Counts',
            link: '/inventory/transaction-reports/count',
            parentKey: 'inventory-reports',
          },
          {
            key: 'inventory-transfers-report',
            label: 'Transfers',
            link: '/inventory/transaction-reports/transfer',
            parentKey: 'inventory-reports',
          },
        ],
      },
    ],
  },
];
